import axios, { AxiosInstance, AxiosError } from 'axios';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from '../utils/companySlug';
import { getApiBaseUrl } from '../config/runtime';
import { logRateLimited, errorRateLimited, warnRateLimited } from '../utils/logRateLimit';

// Type definitions for API error responses
interface ApiErrorResponse {
  code?: string;
  errorCode?: string;
  error?: string;
  message?: string;
  isBlocked?: boolean;
  storageState?: any;
  [key: string]: any; // Allow other properties
}

// Extended AxiosError with custom properties
interface ExtendedAxiosError extends AxiosError<ApiErrorResponse> {
  suppressConsoleError?: boolean;
  isNetworkError?: boolean;
  isTimeout?: boolean;
  isConnectionRefused?: boolean;
}

// CRITICAL: Use centralized runtime config for baseURL
// This ensures consistent baseURL resolution across the entire app
// and prevents localhost fallbacks in production
const API_BASE = getApiBaseUrl();

// Throttled network error logger to prevent console spam
let lastNetworkErrorLog = 0;
const NETWORK_ERROR_LOG_COOLDOWN = 10000; // Log at most once per 10 seconds

function logNetworkError(error: AxiosError, throttled: boolean = true): void {
  const now = Date.now();
  if (throttled && (now - lastNetworkErrorLog) < NETWORK_ERROR_LOG_COOLDOWN) {
    return; // Skip logging if within cooldown
  }
  
  lastNetworkErrorLog = now;
  
  const isNetworkError = !error.response;
  const isConnectionRefused = error.code === 'ECONNREFUSED' || 
                              error.code === 'ERR_CONNECTION_REFUSED' ||
                              error.message?.includes('ERR_CONNECTION_REFUSED');
  const isTimeout = error.code === 'ECONNABORTED';
  
  if (isNetworkError || isConnectionRefused || isTimeout) {
    // CRITICAL FIX: Properly serialize error object to prevent "Object" in console
    const errorInfo = {
      code: error.code || 'UNKNOWN',
      message: error.message?.substring(0, 200) || 'No error message',
      url: error.config?.url?.substring(0, 200) || 'Unknown URL',
      method: error.config?.method?.toUpperCase() || 'UNKNOWN',
      isConnectionRefused,
      isTimeout,
      baseURL: API_BASE || 'Not set',
      timestamp: new Date().toISOString(),
      note: isConnectionRefused 
        ? 'Server may be offline or baseURL is incorrect. Check network connection and environment variables.'
        : isTimeout
        ? 'Request timed out. Server may be slow or unresponsive.'
        : 'Network request failed. Check internet connection.',
    };
    
    // Log as separate properties to ensure proper serialization
    console.warn('[apiClient] Network error:', errorInfo);
    
    // Also log error object details if available
    if (error.config) {
      console.warn('[apiClient] Request config:', {
        url: error.config.url,
        method: error.config.method,
        timeout: error.config.timeout,
        baseURL: error.config.baseURL,
      });
    }
  }
}

// Get device-specific default timeout
// Mobile devices need longer timeouts due to slower networks
function getDefaultTimeout(): number {
  if (typeof window === 'undefined') return 10000;
  
  try {
    // Dynamic import to avoid circular dependencies
    const userAgent = navigator.userAgent || '';
    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
                     (window.innerWidth < 768 && 'ontouchstart' in window);
    
    return isMobile ? 15000 : 10000; // 15s for mobile, 10s for desktop
  } catch (e) {
    return 10000; // Fallback to default
  }
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: getDefaultTimeout(), // 10s desktop, 15s mobile - optimized for better UX
});

// Attach bearer token from localStorage if present
apiClient.interceptors.request.use((config) => {
  // CRITICAL: Block all requests if auth is blocked (circuit breaker)
  // BUT: Allow auth-related endpoints (login, password reset, etc.) even when blocked
  if (authBlocked) {
    const url = config.url || '';
    const isAuthEndpoint = 
      url.includes('/rpc/auth/login') ||
      url.includes('/rpc/auth/send-reset-otp') ||
      url.includes('/rpc/auth/reset-password') ||
      url.includes('/rpc/auth/verify-otp') ||
      url.includes('/rpc/auth/verify-2fa') ||
      url.includes('/rpc/auth/check-company-auth') ||
      url.includes('/api/superadmin/login') ||
      url.includes('/api/superadmin/verify-otp') ||
      url.includes('/api/superadmin/send-reset-otp') ||
      url.includes('/api/superadmin/reset-password');
    const isPublicLeadEndpoint = url.includes('/rpc/leads/public/create');
    
    // Allow auth and public endpoints even when auth is blocked
    // (login, reset password, public lead form submission)
    if (!isAuthEndpoint && !isPublicLeadEndpoint) {
      // Create cancel error manually (axios.Cancel is deprecated in axios v1.x)
      const cancelError = new Error('AUTH_BLOCKED');
      cancelError.name = 'CanceledError';
      (cancelError as any).__CANCEL__ = true;
      (cancelError as any).message = 'AUTH_BLOCKED';
      return Promise.reject(cancelError);
    }
  }

  if (typeof window !== 'undefined') {
    const url = config.url || '';
    // CRITICAL: Don't send Authorization header for public auth endpoints
    // These endpoints don't require authentication (login, 2FA verification, etc.)
    const isPublicAuthEndpoint = 
      url.includes('/rpc/auth/login') ||
      url.includes('/rpc/auth/verify-otp') ||
      url.includes('/rpc/auth/verify-2fa') ||
      url.includes('/rpc/auth/send-reset-otp') ||
      url.includes('/rpc/auth/reset-password') ||
      url.includes('/rpc/auth/check-company-auth') ||
      url.includes('/api/superadmin/login') ||
      url.includes('/api/superadmin/verify-otp');
    
    // CRITICAL: Don't send Authorization header for public proposal endpoints
    const isPublicProposalEndpoint = url.includes('/rpc/public/proposals');
    // Public lead form (landing page) - no auth
    const isPublicLeadEndpoint = url.includes('/rpc/leads/public/create');
    
    // Only add Authorization header if it's NOT a public endpoint
    if (!isPublicAuthEndpoint && !isPublicProposalEndpoint && !isPublicLeadEndpoint) {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // Explicitly remove Authorization header for public endpoints
      if (config.headers) {
        delete config.headers.Authorization;
      }
    }
  }
  return config;
}, (error) => {
  // Handle request cancellation due to auth block (e.g. public lead submit when auth blocked)
  if (error?.message === 'AUTH_BLOCKED' || error?.__CANCEL__ === true) {
    return Promise.reject(error);
  }
  return Promise.reject(error);
});

// ====================================================================
// GLOBAL AUTH CIRCUIT BREAKER
// ====================================================================
// Prevents infinite 401 loops by blocking all requests when SESSION_REVOKED is detected
// ====================================================================

let authBlocked = false;
let lastAuthBlockAt = 0;
let sessionExpiredModalShown = false;

/**
 * Reset auth block (call after successful login)
 */
export function resetAuthBlock(): void {
  authBlocked = false;
  lastAuthBlockAt = 0;
  sessionExpiredModalShown = false;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth:reset'));
  }
}

/**
 * Check if auth is currently blocked
 */
export function isAuthBlocked(): boolean {
  return authBlocked;
}

// CRITICAL FIX: Global 401 handler to prevent infinite 401 loops
let isHandling401 = false;
let last401Time = 0;
const COOLDOWN_MS = 5000; // 5 second cooldown between 401 handling

// Track last SESSION_REVOKED error time to suppress repeated logs
let lastSessionRevokedLog = 0;
const SESSION_REVOKED_LOG_COOLDOWN = 5000; // Only log once per 5 seconds

// CRITICAL FIX: Track manual logout timestamp to ignore stale 401s
// When user manually logs out, we ignore 401s for a short period to prevent
// stale requests (that were in flight before logout) from triggering false logouts
const MANUAL_LOGOUT_COOLDOWN_MS = 5000; // 5 seconds - ignore 401s after manual logout

// ROOT FIX: Central auth helpers
/**
 * Get stored token and validate it's a real token (not "null" or "undefined" string)
 * Returns null if no token exists or if token value is invalid
 */
function getStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
  
  // Check if token exists and is not a falsy string value
  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    return null;
  }
  
  return token;
}

/**
 * Check if user has a real stored token
 */
function hasRealStoredToken(): boolean {
  return !!getStoredToken();
}

/**
 * Logout reason types
 */
type LogoutReason =
  | 'session-expired'
  | 'token-invalid'
  | 'single-session-conflict'
  | 'manual-logout'
  | string;

/**
 * ALLOWED REDIRECT REASONS:
 * - Manual logout: 'manual_click', 'user_logout', 'manual'
 * - Blocked users: 'blocked', 'user-blocked' (ONLY valid auto-redirect scenario)
 * All other reasons (session conflict, token expired, etc.) are BLOCKED
 */
const ALLOWED_REDIRECT_REASONS = [
  'manual_click',
  'user_logout',
  'manual', // Legacy support
  'blocked', // BLOCKED user flow - the ONLY valid auto-redirect
  'user-blocked', // Alternative name for blocked
] as const;

/**
 * Unified redirect to login helper
 * Handles company slug resolution and logoutReason parameter
 * CRITICAL FIX: Handles superadmin routes correctly
 */
function redirectToLogin(options?: { reason?: LogoutReason | null }) {
  const { reason } = options || {};
  
  // 🔥 CRITICAL: ONLY allow manual redirect OR blocked user redirect
  // This must be checked BEFORE any redirect logic
  if (!reason || !ALLOWED_REDIRECT_REASONS.includes(reason as any)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[apiClient][redirectToLogin][AUTO-REDIRECT-BLOCKED] Auto redirect blocked. Only manual redirect or blocked user redirect allowed:', {
        reason,
        pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
        allowedReasons: ALLOWED_REDIRECT_REASONS,
        note: 'Auto redirect is disabled except for BLOCKED users. User must click Logout button to logout.',
      });
    }
    // DO NOT redirect, DO NOT clear anything
    // Just return early and ignore the redirect request completely.
    return;
  }

  const currentHref = window.location.href;
  const currentPath = window.location.pathname;

  // CRITICAL FIX: Check if this is a superadmin route
  const isSuperadminRoute = currentPath.startsWith('/superadmin');
  
  if (isSuperadminRoute) {
    // For superadmin routes, redirect to /superadmin/login
    const url = new URL(window.location.origin);
    url.pathname = '/superadmin/login';
    
    // For superadmin, use 'superadmin_auth_failed' as default reason
    const finalReason = reason || 'superadmin_auth_failed';
    if (finalReason) {
      url.searchParams.set('logoutReason', finalReason);
    }
    
    // Cache-buster to avoid stubborn browser cache issues
    url.searchParams.set('_cb', Date.now().toString());
    
    window.location.href = url.toString();
    return;
  }

  // Extract company slug from URL (for regular company routes)
  const slugFromUrl = getCompanySlugFromLocation();
  const env = getRuntimeEnvFromHostname();
  const company = slugFromUrl || resolveActiveCompanySlug({ env }) || '';

  if (!company) {
    console.warn('[apiClient][redirectToLogin] No company slug available, cannot redirect');
    return;
  }

  const url = new URL(window.location.origin);
  url.pathname = `/${company}/login`;

  if (reason) {
    url.searchParams.set('logoutReason', reason);
  }

  // Cache-buster to avoid stubborn browser cache issues
  url.searchParams.set('_cb', Date.now().toString());

  window.location.href = url.toString();
}

/**
 * Check if user is on login page
 */
function isOnLoginPage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.includes('/login') || 
         window.location.pathname.includes('/auth/login') ||
         /^\/(?:superadmin|[^/]+)\/login(?:\?|$)/.test(window.location.pathname);
}

/**
 * PHASE 5: Debug helper to trace auth errors and logout triggers
 * Logs detailed context about 401/403 errors to help diagnose logout issues
 */
function debugAuthErrorContext(error: any) {
  if (typeof window === 'undefined') return;
  
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const code = data?.code || data?.errorCode;
  const message = data?.message || data?.error || '';
  const url = error?.config?.url;
  const method = error?.config?.method;
  const t1 = localStorage.getItem('token');
  const t2 = localStorage.getItem('accessToken');
  const hasToken = Boolean(t1 && t1 !== 'null' && t1 !== 'undefined') || 
                   Boolean(t2 && t2 !== 'null' && t2 !== 'undefined');

  // Best-effort company slug extractor
  let companySlug: string | null = null;
  try {
    const href = window.location.href;
    const match = href.match(/https?:\/\/[^/]+\/([^/]+)/);
    companySlug = match ? match[1] : null;
  } catch {
    // ignore
  }

  console.groupCollapsed(
    '%c[apiClient][AUTH-401] Logout trigger',
    'color: #ff4d4f; font-weight: bold;',
  );
/*   console.log('Status:', status);
  console.log('Code:', code);
  console.log('Message:', message);
  console.log('Request:', method?.toUpperCase?.(), url);
  console.log('Has stored token:', hasToken, { tokenKey1: t1, tokenKey2: t2 });
  console.log('Current location:', window.location.href);
  console.log('Company slug guess:', companySlug);
  console.log('Full error response data:', data); */
  console.groupEnd();
}

// Response interceptor – enhance error information and extract storageState
apiClient.interceptors.response.use(
  (response) => {
    // 🔥 DEBUG: Log successful responses to trace flow
    // console.log('[API SUCCESS]', {
    //   url: response.config?.url?.substring(0, 200),
    //   status: response.status,
    //   method: response.config?.method,
    //   hasData: !!response.data,
    //   dataKeys: response.data ? Object.keys(response.data) : [],
    // });
    
    // Extract storageState from RPC responses
    if (response.data?.storageState && typeof window !== 'undefined') {
      // Dispatch custom event for StorageStateContext to listen
      window.dispatchEvent(new CustomEvent('storageStateUpdate', {
        detail: response.data.storageState
      }));
    }
    // 🔥 CRITICAL: Always return response to ensure promise chain continues
    return response;
  },
  (error: AxiosError) => {
        // AUTH_BLOCKED is intentional (request cancelled when auth circuit breaker is active) - never log as network error
        const isAuthBlocked = (error as any)?.message === 'AUTH_BLOCKED' || (error as any)?.__CANCEL__ === true;
        if (isAuthBlocked) {
          (error as any).suppressConsoleError = true;
          return Promise.reject(error);
        }
        // 🔥 CRITICAL: Handle network errors first (no response = network issue)
        const isNetworkError = !error.response;
        const isConnectionRefused = error.code === 'ECONNREFUSED' || 
                                    error.code === 'ERR_CONNECTION_REFUSED' ||
                                    error.message?.includes('ERR_CONNECTION_REFUSED') ||
                                    error.message?.includes('Network Error');
        const isTimeout = error.code === 'ECONNABORTED' || 
                         error.message?.includes('timeout') ||
                         error.message?.includes('TIMEOUT');
        
        // Detect mobile device for better error messages
        let isMobile = false;
        if (typeof window !== 'undefined') {
          try {
            const userAgent = navigator.userAgent || '';
            isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
                       (window.innerWidth < 768 && 'ontouchstart' in window);
          } catch (e) {
            // Ignore detection errors
          }
        }
        
        // Log network errors (throttled to prevent spam)
        if (isNetworkError || isConnectionRefused || isTimeout) {
          logNetworkError(error, true);
          // Mark as suppressed to prevent duplicate logging
          (error as any).suppressConsoleError = true;
          // Mark error type for better error handling in callers
          (error as any).isNetworkError = isNetworkError;
          (error as any).isTimeout = isTimeout;
          (error as any).isConnectionRefused = isConnectionRefused;
          (error as any).isMobile = isMobile; // Add mobile flag for better error handling
          // Continue processing - network errors still need to be handled by callers
        }
        
        // 🔥 CRITICAL DEBUG: Log ALL API errors to track what's happening
        const status = error?.response?.status;
        const url = error?.config?.url;
        const data = error?.response?.data as ApiErrorResponse | undefined;
        const errorCode = data?.code || data?.errorCode;
        const errorMessage = data?.error || data?.message || '';
        
        // Check if this is a SESSION_REVOKED error
        const isSessionRevokedError = errorCode === 'SESSION_REVOKED' || 
                                     errorMessage === 'SESSION_REVOKED' ||
                                     (typeof errorMessage === 'string' && errorMessage.includes('SESSION_REVOKED'));
        
        // Suppress repeated SESSION_REVOKED errors to reduce console noise
        // Only log once per cooldown period
        const now = Date.now();
        const shouldSuppressSessionRevoked = isSessionRevokedError && 
                                            (now - lastSessionRevokedLog) < SESSION_REVOKED_LOG_COOLDOWN;
        
        if (isSessionRevokedError && !shouldSuppressSessionRevoked) {
          lastSessionRevokedLog = now;
        }
        
        // Only log meaningful errors (not network errors with undefined status)
        // Suppress errors that are expected (network failures, timeouts, etc.)
        const hasNoStatus = status === undefined;
        
        // Only log if:
        // 1. Not suppressed
        // 2. Not a network error with undefined status (expected when server is down)
        // 3. Not a timeout (expected in some cases)
        // 4. Has meaningful error information
        const shouldLog = !(error as ExtendedAxiosError).suppressConsoleError && 
                         !shouldSuppressSessionRevoked &&
                         !(isNetworkError && hasNoStatus && !isTimeout) &&
                         (status || errorCode || errorMessage || isTimeout);
        
        if (shouldLog) {
          // Rate-limited logging: same error key logs at most once per 15s
          const errorKey = `api-error-${status || 'network'}-${errorCode || 'none'}`;
          logRateLimited(errorKey, () => {
            const errorInfo = {
              url: url?.substring(0, 200),
              status,
              errorCode,
              errorMessage: errorMessage?.substring(0, 200),
              method: error?.config?.method,
              isNetworkError,
              isTimeout,
              ...(isSessionRevokedError ? { note: 'SESSION_REVOKED - will trigger logout if conditions met' } : {}),
            };
            return `[API ERROR] ${JSON.stringify(errorInfo)}`;
          });
        }
    
    // Suppress console errors for 500 errors on email endpoints (email service might not be configured)
    if (error.response?.status === 500 && error.config?.url?.includes('/rpc/email/')) {
      // Mark error as suppressed to prevent console spam
      (error as ExtendedAxiosError).suppressConsoleError = true;
    }
    
    // Suppress console errors for 500/404 errors on logo endpoints (logo might not exist or server error)
    if ((error.response?.status === 500 || error.response?.status === 404) && 
        error.config?.url?.includes('/attachments/logo')) {
      // Mark error as suppressed to prevent console spam - logo errors are expected
      (error as ExtendedAxiosError).suppressConsoleError = true;
    }
    
    // CRITICAL: Suppress 403 errors on failed logins endpoint
    // Users without 'view_failed_logins' permission will get 403, which is expected behavior
    // The query should be disabled, but this prevents console noise if it somehow runs
    if (error.response?.status === 403 && 
        error.config?.url?.includes('/rpc/settings/admin/failed-logins')) {
      // Mark error as suppressed - this is expected when user lacks permission
      (error as ExtendedAxiosError).suppressConsoleError = true;
    }
    
    // 🔥 CRITICAL: Handle 503 Service Unavailable - server is temporarily unavailable
    if (status === 503) {
      // Suppress console errors for 503 - these are server issues, not client errors
      (error as ExtendedAxiosError).suppressConsoleError = true;
      
      // Mark as server unavailable for better error handling
      (error as any).isServerUnavailable = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ApiInterceptor] 503 Service Unavailable - server is temporarily unavailable:', {
          url: url?.substring(0, 200),
          errorMessage: errorMessage?.substring(0, 200),
          note: 'Server may be down, overloaded, or under maintenance. Request will not be retried automatically.',
        });
      }
      return Promise.reject(error);
    }
    
    // 🔥 CRITICAL: NEVER logout on 500 errors - these are server errors, not auth errors
    if (status === 500) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ApiInterceptor] 500 error detected - NOT treating as auth error, NOT logging out:', {
          url: url?.substring(0, 200),
          errorMessage: errorMessage?.substring(0, 200),
        });
      }
      return Promise.reject(error);
    }
    
    // 🔥 CRITICAL: Handle 409 (CONFLICT) - don't auto-logout, let user decide
    // 409 usually means OTHER_SESSION_ACTIVE - this should be handled gracefully
    if (status === 409) {
      const isOtherSessionActive = errorCode === 'OTHER_SESSION_ACTIVE' || 
                                   errorCode === 'USER_ALREADY_LOGGED_IN' ||
                                   (typeof errorMessage === 'string' && errorMessage.includes('OTHER_SESSION_ACTIVE'));
      
      // Check if user just logged in - if so, this might be a false positive
      const justLoggedIn = typeof window !== 'undefined' && sessionStorage.getItem('justLoggedIn');
      const justLoggedInTime = typeof window !== 'undefined' && sessionStorage.getItem('justLoggedInTime');
      const isRecentLogin = justLoggedIn === 'true' && justLoggedInTime && 
        (Date.now() - parseInt(justLoggedInTime, 10)) < 30000; // Within last 30 seconds
      
      if (isRecentLogin && isOtherSessionActive) {
        // User just logged in - this 409 might be from a stale request
        // Don't logout - let the login flow complete
        if (process.env.NODE_ENV === 'development') {
          console.warn('[ApiInterceptor] 409 detected but user just logged in (within 30s), ignoring:', {
            url: url?.substring(0, 200),
            errorCode,
            isRecentLogin,
          });
        }
        return Promise.reject(error);
      }
      
      // For non-recent logins, still don't auto-logout on 409
      // The login page should handle this with a dialog
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ApiInterceptor] 409 error detected - NOT auto-logging out, letting login page handle:', {
          url: url?.substring(0, 200),
          errorCode,
          errorMessage: errorMessage?.substring(0, 200),
        });
      }
      return Promise.reject(error);
    }
    
      // 🔥 CRITICAL: NEVER logout on 400 errors unless they're explicitly auth-related
      if (status === 400) {
        // Only treat as auth error if it has an explicit auth error code
        // NOTE: OTHER_SESSION_ACTIVE is NOT treated as an auth error - user should stay logged in
        const isAuth400 = errorCode === 'INVALID_TOKEN' || 
                         errorCode === 'SESSION_EXPIRED' ||
                         errorCode === 'SESSION_REVOKED';
                         // REMOVED: errorCode === 'OTHER_SESSION_ACTIVE' - conflict sessions should NOT trigger logout
        
        if (!isAuth400) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[ApiInterceptor] 400 error detected - NOT treating as auth error, NOT logging out:', {
              url: url?.substring(0, 200),
              errorCode,
              errorMessage: errorMessage?.substring(0, 200),
            });
          }
          return Promise.reject(error);
        }
      }
      
      // 🔥 CRITICAL: Handle 403 blocked user - this is the ONLY valid auto-logout scenario
      if (error.response?.status === 403 && typeof window !== 'undefined') {
        const data = error.response?.data as ApiErrorResponse | undefined;
        const isBlocked = data?.isBlocked === true || 
                         data?.code === 'BLOCKED_USER' ||
                         data?.code === 'BLOCKED';
        
        if (isBlocked) {
          // Only call debugAuthErrorContext for blocked users
          debugAuthErrorContext(error);
          console.warn('[apiClient][403] User is BLOCKED - triggering blocked user flow');
          
          // Emit blocked event to trigger BlockedUserSocketModal
          window.dispatchEvent(new CustomEvent('user:blocked', { 
            detail: {
              message: data?.error || data?.message || 'Your access has been blocked by the administrator.',
              isBlocked: true,
              userId: null,
              source: 'apiClient-403'
            }
          }));
          
          // DO NOT logout here - let BlockedUserSocketModal handle it with countdown
          return Promise.reject(error);
        }
        
        // For regular 403 permission errors (not blocked user), suppress console spam
        // These are expected when user doesn't have permission for certain endpoints
        const url = error.config?.url || '';
        const isPermissionError = url.includes('/rpc/leads/') || 
                                 url.includes('/rpc/agents/') ||
                                 url.includes('/rpc/settings/admin/failed-logins');
        
        if (isPermissionError) {
          // Mark as suppressed to prevent console spam - permission errors are expected
          (error as ExtendedAxiosError).suppressConsoleError = true;
          // DO NOT call debugAuthErrorContext - this is not an auth issue
          return Promise.reject(error);
        }
      }
    
    // 🔥 CRITICAL: 401 handling - NO AUTO LOGOUT (except for BLOCKED users)
    // All 401 errors are handled WITHOUT logging out or redirecting
    // Only manual logout (via Logout button) or BLOCKED user flow is allowed
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      // PHASE 5: Log detailed context for debugging
      debugAuthErrorContext(error);
      
      const status = error.response?.status;
      const data = error.response?.data as ApiErrorResponse | undefined;
      const code = data?.code || data?.errorCode;
      const errorMessage = data?.error || data?.message || '';
      const requestUrl = error.config?.url || '';
      
      // 🔥 CRITICAL: Check if this is a login request - if so, just return error
      // Login requests can return 401 for wrong password, etc. - this is normal
      const isLoginRequest = requestUrl.includes('/rpc/auth/login') || 
                            requestUrl.includes('/auth/login') ||
                            requestUrl.includes('/api/auth/login');
      
      if (isLoginRequest) {
        return Promise.reject(error); // Just return error to Login.tsx, don't logout
      }
      
      // Don't handle 401 on login page - user is already logging in
      if (isOnLoginPage()) {
        return Promise.reject(error);
      }
      
      // Check for manual logout cooldown
      const isManualLogout = sessionStorage.getItem('manualLogout') === 'true' ||
                             localStorage.getItem('manualLogout') === 'true';
      const recentLogoutTime = sessionStorage.getItem('manualLogoutTime') || 
                               localStorage.getItem('manualLogoutTime');
      const isRecentManualLogout = isManualLogout && recentLogoutTime && 
        (Date.now() - parseInt(recentLogoutTime, 10)) < MANUAL_LOGOUT_COOLDOWN_MS;
      
      if (isRecentManualLogout) {
        return Promise.reject(error);
      }
      
      // 🔥 CRITICAL: Check if user is BLOCKED - this is the ONLY valid auto-logout scenario
      const isBlocked = data?.isBlocked === true || 
                       data?.code === 'BLOCKED_USER' ||
                       data?.code === 'BLOCKED' ||
                       errorMessage?.toLowerCase().includes('blocked') ||
                       errorMessage?.includes('נחסם');
      
      if (isBlocked) {
        console.warn('[apiClient][401] User is BLOCKED - triggering blocked user flow:', {
          code,
          errorMessage: errorMessage?.substring(0, 200),
          url: requestUrl,
        });
        
        // Emit blocked event to trigger BlockedUserSocketModal
        window.dispatchEvent(new CustomEvent('user:blocked', { 
          detail: {
            message: errorMessage || 'Your access has been blocked by the administrator.',
            isBlocked: true,
            userId: null,
            source: 'apiClient-401'
          }
        }));
        
        // DO NOT logout here - let BlockedUserSocketModal handle it with countdown
        return Promise.reject(error);
      }
      
      // 🔥 CRITICAL: Handle SESSION_REVOKED with circuit breaker
      const isSessionRevokedError = code === 'SESSION_REVOKED' || 
                                   errorMessage === 'SESSION_REVOKED' ||
                                   (typeof errorMessage === 'string' && errorMessage.includes('SESSION_REVOKED'));
      
      if (isSessionRevokedError && !authBlocked) {
        // Activate circuit breaker
        authBlocked = true;
        lastAuthBlockAt = Date.now();
        
        console.warn('[apiClient][401] SESSION_REVOKED detected - activating auth circuit breaker', {
          code,
          errorMessage: errorMessage?.substring(0, 200),
          url: requestUrl,
        });
        
        // Show session expired modal (only once)
        if (!sessionExpiredModalShown && typeof window !== 'undefined') {
          sessionExpiredModalShown = true;
          window.dispatchEvent(new CustomEvent('auth:session-expired', {
            detail: {
              message: 'הסשן פג תוקף. נא להתחבר מחדש',
              errorCode: code,
            }
          }));
        }
        
        // Mark error so React Query doesn't retry
        (error as any).isSessionRevoked = true;
        (error as any).suppressConsoleError = true; // Already logged above
        
        return Promise.reject(error);
      }
      
      // If auth is already blocked, suppress this error
      if (authBlocked) {
        (error as any).suppressConsoleError = true;
        return Promise.reject(error);
      }
      
      // 🔥 CRITICAL: NO AUTO LOGOUT for non-blocked users - just log the error and reject
      // The calling code can handle the error (show toast, retry, etc.)
      // User stays logged in - only manual logout is allowed
      console.warn('[apiClient][401] 401 error detected - NOT logging out (auto logout disabled):', {
        code,
        errorMessage: errorMessage?.substring(0, 200),
        url: requestUrl,
        note: 'Auto logout is disabled except for BLOCKED users. User must click Logout button to logout.',
      });
      
      // DO NOT clear tokens, DO NOT redirect, DO NOT set logoutReason
      // Just reject the error and let the calling code handle it
      return Promise.reject(error);
    }
    
    // Enhance error with additional context
    if (error.response) {
      // Server responded with error status
      (error as any).isNetworkError = false;
      (error as any).isTimeout = false;
      (error as any).isConnectionRefused = false;
      
      // Also check for storageState in error responses
      const responseData = error.response.data as ApiErrorResponse | undefined;
      if (responseData?.storageState && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('storageStateUpdate', {
          detail: responseData.storageState
        }));
      }
    } else if (error.request) {
      // Request made but no response received
      (error as any).isNetworkError = true;
      (error as any).isTimeout = error.code === 'ECONNABORTED';
      (error as any).isConnectionRefused = error.code === 'ECONNREFUSED' || 
                                           error.code === 'ERR_CONNECTION_REFUSED' ||
                                           error.message?.includes('ERR_CONNECTION_REFUSED');
      
      // CRITICAL: Suppress timeout/connection errors toast notifications
      // Mark timeout errors so they can be handled silently by error handlers
      if ((error as any).isTimeout || (error as any).isConnectionRefused) {
        (error as any).suppressToast = true; // Flag to suppress toast in error handlers
      }
    } else {
      // Error setting up request
      (error as any).isNetworkError = false;
      (error as any).isTimeout = false;
      (error as any).isConnectionRefused = false;
    }
    
    return Promise.reject(error);
  }
);

// ====================================================================
// QA CHECKLIST – Auth / logoutReason root fix
// ====================================================================
// 
// 1. Fresh incognito window -> go to /test/home:
//    - Redirects to /test/login WITHOUT ?logoutReason
//    - Login page has NO red error banner.
// 
// 2. Log in normally, then let the session expire (or force SESSION_EXPIRED from backend):
//    - Next request returns 401 with code "SESSION_EXPIRED".
//    - Frontend clears tokens, sets logoutReason='session-expired',
//      redirects to /test/login?logoutReason=session-expired.
//    - Login shows the red banner exactly once; refreshing /test/login shows clean form.
// 
// 3. Open a second incognito window and go directly to /test/login:
//    - No banner unless there is an explicit logoutReason in the URL.
// 
// 4. Trigger single-session conflict (SESSION_REVOKED / OTHER_SESSION_ACTIVE if applicable):
//    - User sees appropriate conflict message (logoutReason='single-session-conflict').
// 
// 5. Manual logout from inside the app:
//    - Sets logoutReason='manual-logout' (if implemented),
//      redirects to /test/login with a friendly message.
// 
// 6. Check console logs to ensure:
//    - NO_TOKEN + no stored token -> '[apiClient][401] NO_TOKEN with no stored token – redirecting to CLEAN login'.
//    - SESSION_EXPIRED -> '[apiClient][401] SESSION_EXPIRED' and proper redirect with reason.
//    - Login consumeLogoutReason is called once per visit with a reason, and then URL is cleaned.
//
// ====================================================================

export default apiClient;
